import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Filter,
  Info,
  Key,
  Pencil,
  Plus,
  Search,
  Shield,
  ShieldAlert,
  Trash2,
  UserCheck,
  X,
} from 'lucide-react';

import {
  useAssignUserPermissionMutation,
  useCreatePermissionMutation,
  useDeletePermissionMutation,
  usePermissionRegistryQuery,
  useToggleUserPermissionMutation,
  useUpdatePermissionMutation,
  useUserPermissionsQuery,
} from '@/client/api/admin/permissions';
import type {
  AdminPermission,
  AdminUserPermission,
  AdminUserSearchUser,
} from '@/client/types/admin/users-orgs';

import { useSearchParams } from 'react-router-dom';

import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { useListFilters } from '@/client/hooks/useListFilters';
import useTabSearchParam from '@/client/hooks/useTabSearchParam';
import SortableTh from '@/client/components/common/SortableTh';
import UserSearchSelect from '@/client/components/common/UserSearchSelect';
import { readStoredUser, writeStoredUser } from '@/client/utils/sessionUser';
import { PERMISSION_CATEGORIES, PERMISSIONS } from '@/client/config/permissions';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// The category vocabulary is owned by the permission catalog so the create/edit
// form, the sidebar/route gates, and the backend registry all agree.
const CATEGORIES = PERMISSION_CATEGORIES;

// Managing permissions is itself a gated power. Uses a real category (`user`)
// so the code can actually be created and granted — a `system.*` code could
// never be, since `system` is not a registered category.
const PERMISSIONS_PERMISSION = PERMISSIONS.systemPermissions;

const USER_PERMISSIONS_STORAGE_KEY = 'ff-admin:user-permissions:selected-user';

// ─── Access-denied state ──────────────────────────────────────────────────────

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Managing permissions requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">
        {PERMISSIONS_PERMISSION}
      </code>{' '}
      permission. Ask an administrator to grant it.
    </p>
  </div>
);

// ─── Create / edit permission dialog ──────────────────────────────────────────

interface PermissionDialogProps {
  open: boolean;
  /** `null` = create, otherwise edit that row. */
  permission: AdminPermission | null;
  onClose: () => void;
}

const PermissionDialog = ({
  open,
  permission,
  onClose,
}: PermissionDialogProps) => {
  const isEdit = permission !== null;
  const [form, setForm] = useState({
    code: permission?.code ?? '',
    name: permission?.name ?? '',
    description: permission?.description ?? '',
    category: permission?.category ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreatePermissionMutation();
  const updateMutation = useUpdatePermissionMutation();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const code = form.code.trim();
    const name = form.name.trim();
    const description = form.description.trim();
    const category = form.category.trim();

    if (!code || !name || !description || !category) {
      setError('Code, name, description and category are all required.');
      return;
    }

    // The backend rejects any code not prefixed with its category and returns a
    // bare 400 `invalid_code` that does not say why. Check it here instead.
    if (!code.startsWith(`${category}.`)) {
      setError(
        `Code must start with "${category}." to match the selected category — e.g. "${category}.view".`
      );
      return;
    }

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({
          permid: permission.permid,
          code,
          name,
          description,
          category,
        });
        toast.success(`Permission “${code}” updated`);
      } else {
        await createMutation.mutateAsync({ code, name, description, category });
        toast.success(`Permission “${code}” created`);
      }
      onClose();
    } catch (err: any) {
      if (err?.status === 400) {
        toast.error(
          'The backend rejected this permission — check that the code is unique and prefixed with its category.'
        );
      } else {
        toast.error(err?.message ?? 'Failed to save the permission.');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-blue-600" />
            {isEdit ? 'Edit permission' : 'Create permission'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="perm-category">
              Category <span className="text-red-500">*</span>
            </Label>
            <select
              id="perm-category"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={form.category}
              onChange={(e) =>
                setForm((p) => ({ ...p, category: e.target.value }))
              }
            >
              <option value="">Select a category</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="perm-code">
              Code <span className="text-red-500">*</span>
            </Label>
            <Input
              id="perm-code"
              placeholder={
                form.category ? `${form.category}.view` : 'category.action'
              }
              value={form.code}
              onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
            />
            <p className="text-xs text-gray-500">
              Must begin with the category followed by a dot.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="perm-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="perm-name"
              placeholder="e.g. View Permissions"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="perm-desc">
              Description <span className="text-red-500">*</span>
            </Label>
            <Input
              id="perm-desc"
              placeholder="What this permission grants"
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? 'Saving…'
                : isEdit
                  ? 'Save changes'
                  : 'Create permission'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Assign permission dialog ─────────────────────────────────────────────────

interface AssignPermissionDialogProps {
  open: boolean;
  uid: number;
  assigned: AdminUserPermission[];
  onClose: () => void;
}

const AssignPermissionDialog = ({
  open,
  uid,
  assigned,
  onClose,
}: AssignPermissionDialogProps) => {
  const [permid, setPermid] = useState('');
  const [search, setSearch] = useState('');
  // Pull the WHOLE registry in one shot (the endpoint has no per_page cap) so
  // the picker shows every permission and the local search covers all of them —
  // previously it fetched only the default first page (~20) and "searched" that
  // slice, hiding most permissions. If the registry ever outgrows this, switch
  // to server-side search (needs a fuzzy `search` param — backend change).
  const { data: permissions = [], isLoading: permsLoading } =
    usePermissionRegistryQuery({ per_page: 1000 });
  const assignMutation = useAssignUserPermissionMutation();

  const assignedIds = useMemo(
    () => new Set(assigned.map((row) => row.permid)),
    [assigned]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const available = permissions.filter((p) => !assignedIds.has(p.permid));
    if (!term) return available;
    return available.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        p.code.toLowerCase().includes(term)
    );
  }, [permissions, assignedIds, search]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!permid) return;
    try {
      await assignMutation.mutateAsync({ uid, permids: [Number(permid)] });
      toast.success('Permission assigned');
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to assign the permission.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-blue-600" />
            Assign permission to user
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>Search permissions</Label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                className="pl-9"
                placeholder="Filter by name or code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="assign-perm">
                Permission <span className="text-red-500">*</span>
              </Label>
              <span className="text-xs text-gray-400">
                {permsLoading
                  ? 'Loading…'
                  : `${filtered.length} available${search ? ' (filtered)' : ''}`}
              </span>
            </div>
            <select
              id="assign-perm"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={permid}
              onChange={(e) => setPermid(e.target.value)}
              size={Math.min(Math.max(filtered.length, 1) + 1, 10)}
            >
              <option value="">Select a permission</option>
              {filtered.map((p) => (
                <option key={p.permid} value={p.permid}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={assignMutation.isPending || !permid}>
              {assignMutation.isPending ? 'Assigning…' : 'Assign permission'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Permissions registry tab ─────────────────────────────────────────────────

interface RegistryProps {
  canManage: boolean;
}

// Backend default page size. The list endpoint offsets/limits by this but
// returns no total, so paging is Next/Prev only (see PAGER below).
const PER_PAGE = 20;

const PermissionsRegistry = ({ canManage }: RegistryProps) => {
  // Category, search and page persist in the URL (namespaced so they don't
  // collide with the User-permissions tab that shares this route).
  const {
    filters,
    handleFilterChange,
    setFilters,
    searchTerm,
    debouncedSearchTerm,
    handleSearch,
    page,
    handlePageChange,
  } = useListFilters<{
    category: string;
    sort_by: string;
    sort_order: 'asc' | 'desc';
  }>({
    initialFilters: { category: '', sort_by: 'permid', sort_order: 'asc' },
    initialLimit: PER_PAGE,
    namespace: 'reg',
  });
  const category = filters.category;
  const search = searchTerm;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminPermission | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminPermission | null>(
    null
  );

  const deleteMutation = useDeletePermissionMutation();
  const {
    data: permissions = [],
    isLoading,
    isFetching,
    isError,
  } = usePermissionRegistryQuery({
    page,
    per_page: PER_PAGE,
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
    ...(category ? { category } : {}),
    ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
  });

  // Search, filter and sort are all backend-driven now, so the rows are exactly
  // what the endpoint returned. No total is returned, so "next" is only known to
  // exist when the current page came back full.
  const rows = permissions;
  const hasNextPage = permissions.length === PER_PAGE;
  const hasPrevPage = page > 1;

  // Category filter changes the result set — handleFilterChange resets to the
  // first page so the user isn't stranded on an out-of-range page.
  const changeCategory = (next: string) => handleFilterChange('category', next);

  const toggleSort = (field: string) => {
    const nextOrder =
      filters.sort_by === field && filters.sort_order === 'asc' ? 'desc' : 'asc';
    setFilters({ sort_by: field, sort_order: nextOrder });
    handlePageChange(1);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteMutation.mutateAsync({ permid: pendingDelete.permid });
      toast.success(`Permission “${pendingDelete.code}” deleted`);
      setPendingDelete(null);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to delete the permission.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={category}
            onChange={(e) => changeCategory(e.target.value)}
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Filter by code, name or description…"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        {canManage && (
          <div className="sm:ml-auto">
            <Button
              className="gap-2"
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Create permission
            </Button>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableTh
                  label="Code"
                  field="code"
                  sortBy={filters.sort_by}
                  sortOrder={filters.sort_order}
                  onToggle={toggleSort}
                />
                <SortableTh
                  label="Name"
                  field="name"
                  sortBy={filters.sort_by}
                  sortOrder={filters.sort_order}
                  onToggle={toggleSort}
                />
                <SortableTh
                  label="Category"
                  field="category"
                  sortBy={filters.sort_by}
                  sortOrder={filters.sort_order}
                  onToggle={toggleSort}
                />
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <td key={j} className="px-6 py-4">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-sm text-red-600"
                  >
                    Could not load the permission registry.
                  </td>
                </tr>
              )}

              {!isLoading && !isError && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-sm text-gray-500"
                  >
                    No permissions found.
                  </td>
                </tr>
              )}

              {!isLoading &&
                !isError &&
                rows.map((perm) => (
                  <tr key={perm.permid} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4">
                      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-800">
                        {perm.code}
                      </code>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {perm.name}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                        {perm.category}
                      </span>
                    </td>
                    <td className="max-w-xs truncate px-6 py-4 text-sm text-gray-500">
                      {perm.description || '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      {canManage ? (
                        <div className="flex items-center justify-end gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(perm);
                              setDialogOpen(true);
                            }}
                            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
                            title="Edit permission"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingDelete(perm)}
                            className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-800"
                            title="Delete permission"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Read-only</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* `GET /api/admin/permissions` offsets/limits by page but returns no
            total, so this is a Next/Prev pager: "next" is only known to exist
            when the current page came back full. Search filters the current
            page only. */}
        {!isLoading && !isError && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500">
              {search ? (
                <>
                  Showing {rows.length} match{rows.length === 1 ? '' : 'es'} on
                  page {page}
                </>
              ) : (
                <>
                  Page {page} · {permissions.length} permission
                  {permissions.length === 1 ? '' : 's'}
                </>
              )}
              {isFetching && <span className="ml-2 text-gray-400">Loading…</span>}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={!hasPrevPage || isFetching}
              >
                Previous
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-medium text-gray-700">
                {page}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={!hasNextPage || isFetching}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {dialogOpen && (
        <PermissionDialog
          // Remount per target so the form initialises from the right row.
          key={editing?.permid ?? 'create'}
          open={dialogOpen}
          permission={editing}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
        />
      )}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => !next && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this permission?</AlertDialogTitle>
            <AlertDialogDescription>
              “{pendingDelete?.code}” will be removed from the registry and every
              user holding it will lose the access it grants. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete permission'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ─── User permissions tab ─────────────────────────────────────────────────────

interface UserPermissionsProps {
  canManage: boolean;
}

const UserPermissions = ({ canManage }: UserPermissionsProps) => {
  // The looked-up uid persists in the URL (?uid=) so a reload reopens the same
  // user. (The local "active" overrides below are session-only by design.)
  const [searchParams, setSearchParams] = useSearchParams();
  const uidParam = searchParams.get('uid');
  const activeUid =
    uidParam && Number.isFinite(Number(uidParam)) && Number(uidParam) > 0
      ? Number(uidParam)
      : null;
  // Rehydrate the last-picked user so a reload keeps showing their name rather
  // than falling back to the bare uid. Only trust the cached user when it still
  // matches the uid in the URL (guards a hand-edited ?uid=).
  const storedUser = readStoredUser(USER_PERMISSIONS_STORAGE_KEY);
  const hydratedUser =
    storedUser && String(storedUser.uid) === uidParam ? storedUser : null;
  const [userSearch, setUserSearch] = useState(
    hydratedUser
      ? `${hydratedUser.first_name} ${hydratedUser.last_name}`.trim()
      : ''
  );
  const [selectedUser, setSelectedUser] = useState<AdminUserSearchUser | null>(
    hydratedUser
  );
  const [showAssign, setShowAssign] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<AdminUserPermission | null>(
    null
  );

  /**
   * `GET /api/admin/user_permissions/<uid>` omits `active` (gap M-5), so the
   * server cannot tell us whether an assignment is live. This map records only
   * what WE changed in this session; anything absent from it is genuinely
   * unknown and is labelled as such rather than assumed active.
   */
  const [localActive, setLocalActive] = useState<Record<number, boolean>>({});

  const {
    data: assignments = [],
    isLoading,
    isFetching,
    isError,
  } = useUserPermissionsQuery(activeUid, activeUid !== null);
  const toggleMutation = useToggleUserPermissionMutation();

  const handleSelectUser = (user: AdminUserSearchUser) => {
    if (!Number.isFinite(user.uid) || user.uid <= 0) {
      toast.error('That user has no valid uid.');
      return;
    }
    setSelectedUser(user);
    setUserSearch(`${user.first_name} ${user.last_name}`.trim());
    writeStoredUser(USER_PERMISSIONS_STORAGE_KEY, user);
    setLocalActive({});
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('uid', String(user.uid));
        return next;
      },
      { replace: true },
    );
  };

  const selectedName = selectedUser
    ? `${selectedUser.first_name} ${selectedUser.last_name}`.trim()
    : null;

  const handleToggle = async () => {
    if (!pendingToggle) return;
    const nextActive = localActive[pendingToggle.upermid] === false;
    try {
      await toggleMutation.mutateAsync({
        upermid: pendingToggle.upermid,
        active: nextActive,
      });
      setLocalActive((prev) => ({
        ...prev,
        [pendingToggle.upermid]: nextActive,
      }));
      toast.success(nextActive ? 'Permission restored' : 'Permission revoked');
      setPendingToggle(null);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to change the permission.');
    }
  };

  const busy = isLoading || isFetching;
  const pendingIsRevoked =
    pendingToggle !== null && localActive[pendingToggle.upermid] === false;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-5 shadow">
        <h3 className="mb-3 text-sm font-semibold text-gray-700">
          Look up user permissions
        </h3>
        <UserSearchSelect
          className="max-w-md"
          value={userSearch}
          onChange={setUserSearch}
          onSelect={handleSelectUser}
        />
      </div>

      {activeUid !== null && (
        <>
          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b px-6 py-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Permissions for{' '}
                  <span className="text-blue-700">
                    {selectedName ?? `uid ${activeUid}`}
                  </span>
                </h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  {assignments.length} assignment
                  {assignments.length === 1 ? '' : 's'}
                </p>
              </div>
              {canManage && (
                <Button
                  size="sm"
                  className="gap-2"
                  onClick={() => setShowAssign(true)}
                >
                  <Plus className="h-4 w-4" />
                  Assign permission
                </Button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Name', 'Code', 'Category', 'State', 'Actions'].map((h) => (
                      <th
                        key={h}
                        className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                          h === 'Actions' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {busy &&
                    Array.from({ length: 4 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 5 }).map((__, j) => (
                          <td key={j} className="px-6 py-4">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))}

                  {!busy && isError && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-12 text-center text-sm text-red-600"
                      >
                        Could not load permissions for that uid.
                      </td>
                    </tr>
                  )}

                  {!busy && !isError && assignments.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-12 text-center text-sm text-gray-500"
                      >
                        No permissions assigned to this user yet.
                      </td>
                    </tr>
                  )}

                  {!busy &&
                    !isError &&
                    assignments.map((row) => {
                      const known = localActive[row.upermid];
                      const revoked = known === false;
                      return (
                        <tr
                          key={row.upermid}
                          className={`hover:bg-gray-50 ${revoked ? 'opacity-60' : ''}`}
                        >
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                            {row.permission.name}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <code className="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-800">
                              {row.permission.code}
                            </code>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                              {row.permission.category}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className={`inline-flex cursor-help items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                      known === undefined
                                        ? 'bg-gray-100 text-gray-600'
                                        : revoked
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-green-100 text-green-800'
                                    }`}
                                  >
                                    {known === undefined
                                      ? 'Unknown'
                                      : revoked
                                        ? 'Revoked (local)'
                                        : 'Active (local)'}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-xs leading-relaxed">
                                    {known === undefined
                                      ? 'The list endpoint omits the `active` field (gap M-5), so the server does not say whether this assignment is live.'
                                      : 'Reflected from the change you made in this session. It has not been confirmed by the server.'}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right">
                            {canManage ? (
                              <button
                                type="button"
                                onClick={() => setPendingToggle(row)}
                                className={`inline-flex items-center gap-1 text-sm font-medium ${
                                  revoked
                                    ? 'text-green-600 hover:text-green-800'
                                    : 'text-red-600 hover:text-red-800'
                                }`}
                                title={
                                  revoked
                                    ? 'Restore permission'
                                    : 'Revoke permission'
                                }
                              >
                                {revoked ? (
                                  <UserCheck className="h-4 w-4" />
                                ) : (
                                  <X className="h-4 w-4" />
                                )}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">
                                Read-only
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showAssign && activeUid !== null && (
        <AssignPermissionDialog
          open={showAssign}
          uid={activeUid}
          assigned={assignments}
          onClose={() => setShowAssign(false)}
        />
      )}

      <AlertDialog
        open={pendingToggle !== null}
        onOpenChange={(next) => !next && setPendingToggle(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingIsRevoked
                ? 'Restore this permission?'
                : 'Revoke this permission?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              “{pendingToggle?.permission.code}” will be{' '}
              {pendingIsRevoked ? 'reactivated for' : 'deactivated for'} uid{' '}
              {activeUid}. The result cannot be verified on reload — the list
              endpoint does not return whether an assignment is active.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggleMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant={pendingIsRevoked ? 'default' : 'destructive'}
              onClick={handleToggle}
              disabled={toggleMutation.isPending}
            >
              {toggleMutation.isPending
                ? 'Working…'
                : pendingIsRevoked
                  ? 'Restore'
                  : 'Revoke'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

type Tab = 'registry' | 'users';

const PermissionsPage = () => {
  useBreadcrumbs([
    { label: 'Users Management', href: '#' },
    { label: 'Permissions' },
  ]);

  // The active tab persists in the URL (?tab=) so a reload stays on the same tab.
  const [activeTab, setActiveTab] = useTabSearchParam<Tab>(['registry', 'users'], 'registry');
  const { hasPermission, isSuperAdmin, isLoading: permLoading } =
    useHasPermission(PERMISSIONS_PERMISSION);

  // `useHasPermission` does not fold isSuperAdmin into hasPermission.
  const canManage = hasPermission || isSuperAdmin;
  const permissionKnownAbsent = !permLoading && !canManage;

  const TabButton = ({
    id,
    label,
    icon: Icon,
  }: {
    id: Tab;
    label: string;
    icon: typeof Key;
  }) => (
    <button
      type="button"
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
        activeTab === id
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );

  if (permissionKnownAbsent) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <AccessDenied />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        <div className="mb-6">
          <div className="mb-1 flex items-center gap-3">
            <Shield className="h-7 w-7 text-blue-700" />
            <h1 className="text-2xl font-bold text-gray-900">Permissions</h1>
          </div>
          <p className="ml-10 text-sm text-gray-500">
            Manage system permissions and assign them to users
          </p>
        </div>

        <div className="rounded-t-lg border border-b-0 bg-white shadow">
          <div className="flex border-b px-2">
            <TabButton id="registry" label="Permissions registry" icon={Key} />
            <TabButton id="users" label="User permissions" icon={UserCheck} />
          </div>
        </div>

        <div className="rounded-b-lg bg-gray-50 p-1">
          <div className="pt-4">
            {activeTab === 'registry' && (
              <PermissionsRegistry canManage={canManage} />
            )}
            {activeTab === 'users' && <UserPermissions canManage={canManage} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionsPage;
